/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useEffect } from "react";
import FormFader from "../Common/Fader/FormFader";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import FormSubmit from "../Common/FormSubmit/FormSubmit";
import Hidden from "@mui/material/Hidden";
import EnforceIcon from "../../../assets/icons/contract/toggleable/IconEnforce";
import AdvisoryIcon from "../../../assets/icons/contract/toggleable/IconQuestionmarkBubble";
import Person from "../Common/Person/Person";
import _includes from "lodash/includes";
import Address from "../Common/Address/Address";
import PersonIcon from "../../../assets/icons/provision/toggleable/IconPerson";
import OtherPersonIcon from "../../../assets/icons/contract/toggleable/IconMultiplePeople";
import FamilyIcon from "../../../assets/icons/generic/toggleable/IconFamilyDetailed";
import BaseEnforceClaim from "./BaseEnforceClaim";

import {
  dateFieldDefault,
  isLessThen18YearsAgo,
  isPhoneNumber,
  minStringLength,
  pastOrSameDateDefault,
  requiredFieldDefault,
  requiredValidator,
} from "../../../services/validationRules";
import { useForm } from "../../../provider/Form/FormProvider";
import Group from "../../Wizard/Group";
import ValidatorTextField from "../../FormFields/ValidatorTextField";
import Typography from "@mui/material/Typography";
import FormLegend from "../Common/FormLegend/FormLegend";
import ValidatorDateField from "../../FormFields/ValidatorDateField";
import CheckIcon from "../../../assets/icons/common/toggleable/IconCheck";
import CrossIcon from "../../../assets/icons/common/toggleable/IconClose";
import FormSubmitProps from "../../../types/FormSubmitProps";
import { AbstractCase } from "../../../types/Entities/AbstractCase";
import { translate } from "../../../services/translations/translations";
import wizardStyle from "../wizardStyle";
import { Box } from "@mui/material";

type BaseProps = {
  submitProps: FormSubmitProps;
  product: AbstractCase;
  hideCaseCategories: boolean;
  alternativeHeadline?: string;
};

export default function Base({ submitProps, product, hideCaseCategories = false, alternativeHeadline }: BaseProps) {
  const { values, handleSubmit, handleChange } = useForm();

  useEffect(() => {
    if (values.legalRepresentativeType === "me") {
      handleChange({
        target: {
          name: "clientLegalRepresentative.gender",
          value: values.customer.person.gender,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "clientLegalRepresentative.givenName",
          value: values.customer.person.givenName,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "clientLegalRepresentative.familyName",
          value: values.customer.person.familyName,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "clientLegalRepresentative.residenceAddress.streetAddress",
          value: values.customer.person.residenceAddress.streetAddress,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "clientLegalRepresentative.residenceAddress.postalCode",
          value: values.customer.person.residenceAddress.postalCode,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "clientLegalRepresentative.residenceAddress.addressLocality",
          value: values.customer.person.residenceAddress.addressLocality,
          type: "text",
        },
      });
      return;
    }
    handleChange({
      target: {
        name: "clientLegalRepresentative.gender",
        value: "",
        type: "text",
      },
    });
    handleChange({
      target: {
        name: "clientLegalRepresentative.givenName",
        value: "",
        type: "text",
      },
    });
    handleChange({
      target: {
        name: "clientLegalRepresentative.familyName",
        value: "",
        type: "text",
      },
    });
    handleChange({
      target: {
        name: "clientLegalRepresentative.residenceAddress.streetAddress",
        value: "",
        type: "text",
      },
    });
    handleChange({
      target: {
        name: "clientLegalRepresentative.residenceAddress.postalCode",
        value: "",
        type: "text",
      },
    });
    handleChange({
      target: {
        name: "clientLegalRepresentative.residenceAddress.addressLocality",
        value: "",
        type: "text",
      },
    });
  }, [values.legalRepresentativeType]);

  useEffect(() => {
    if (values.affectedPartyType !== "coinsured") {
      handleChange({
        target: {
          name: "client.gender",
          value: "",
        },
      });
      handleChange({
        target: {
          name: "client.givenName",
          value: "",
        },
      });
      handleChange({
        target: {
          name: "client.familyName",
          value: "",
        },
      });
      handleChange({
        target: {
          name: "client.birthDate",
          value: null,
        },
      });
      handleChange({
        target: {
          name: "clientWasMinorAtContractDate",
          value: null,
        },
      });
      return;
    }
  }, [values.affectedPartyType]);

  useEffect(() => {
    if (values.affectedPartyType !== "customer" || values.howManyClients === "oneClient") {
      handleChange({
        target: {
          name: "additionalClients",
          value: [],
        },
      });
      return;
    }
    if (values.howManyClients === "twoClients" && !values.additionalClients?.length) {
      handleChange({
        target: {
          name: "additionalClients",
          value: product.additionalClients?.length
            ? product.additionalClients
            : [
                {
                  givenName: "",
                  familyName: "",
                  gender: "",
                },
              ],
        },
      });
    }
  }, [values.howManyClients, values.affectedPartyType]);

  const getCaseCategoryOptions = () => {
    let options = [
      {
        labelText: "Ich möchte eine Forderung durchsetzen",
        labelIcon: <EnforceIcon />,
        labelIconActive: <EnforceIcon active />,
        value: "enforceClaim",
        optionHintText:
          "Ich möchte etwas mit rechtlicher Unterstützung durchsetzen lassen. Beispiele: Ich wurde von einem Hund gebissen. | Mein Nachbar macht ständig Lärm. | Jemand veröffentlicht Unwahrheiten über mich.",
      },
    ];
    if (product.categoryChange === null || product.categoryChange.previousCaseCategory !== "advisory") {
      options = [
        {
          labelText: "Ich benötige zunächst nur eine Erstberatung",
          labelIcon: <AdvisoryIcon />,
          labelIconActive: <AdvisoryIcon active />,
          value: "advisory",
          optionHintText:
            "Ich möchte nichts weiter veranlassen, sondern habe nur eine kurze Frage im Bereich Zivilrecht und benötige hierfür eine Erstberatung.",
        },
        ...options,
      ];
    }
    return options;
  };

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          <Hidden smUp>
            <Typography variant={"h4"} sx={wizardStyle.headline}>
              Ihr Anliegen
            </Typography>
          </Hidden>
          <Grid container justifyContent={"flex-start"} alignItems={"baseline"}>
            <Grid item xs={12}>
              <FormLegend>
                <strong>{alternativeHeadline !== null ? alternativeHeadline : "Allgemeines zu Ihrem Anliegen"}</strong>
              </FormLegend>
            </Grid>
            {!hideCaseCategories && (
              <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
                <Group
                  type="radio"
                  name="caseCategory"
                  question="Wie können wir Ihnen weiterhelfen?"
                  options={getCaseCategoryOptions()}
                  validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                />
              </Grid>
            )}

            {values.caseCategory && (
              <>
                <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
                  <FormLegend>
                    {values.caseCategory === "advisory"
                      ? "Bitte schildern Sie hier nur kurz in zwei bis drei Sätzen, was passiert ist und was Sie erreichen möchten. Detaillierte Angaben, wie Namen oder Adressen sind für eine Erstberatung zunächst nicht zwingend notwendig."
                      : "Bitte schildern Sie zunächst nur kurz in zwei bis drei Sätzen, was passiert ist und was Sie erreichen möchten. Detaillierte Angaben, wie Namen, Adressen oder weiterführende Informationen zum Sachverhalt, müssen Sie hier noch nicht machen. Diese können Sie in einem späteren Schritt ergänzen."}
                  </FormLegend>
                </Grid>
                <Grid item xs={12}>
                  <ValidatorTextField
                    name="initialClientDescriptionAndObjectives"
                    label="Beschreibung und Zielsetzung"
                    placeholder="Beispiel: Mein Nachbar betreibt regelmäßig sehr laute Feiern bis spät in die Nacht. Trotz mehrfacher Gespräche hat sich daran nichts geändert. Ich möchte, dass diese nächtliche Ruhestörung künftig unterlassen wird."
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      maxLength: 500,
                    }}
                    multiline
                    rows={8}
                    validators={[requiredValidator("Bitte beschreiben Sie was passiert ist und was Sie erreichen möchten.")]}
                    helperText={
                      "[" +
                      (values.initialClientDescriptionAndObjectives?.length ?? 0) +
                      "/500]" +
                      ((values.initialClientDescriptionAndObjectives?.length ?? 0) < 50 ? " Bitte geben Sie mindestens 50 Buchstaben ein" : "")
                    }
                  />
                </Grid>

                <Person path="customer.person" headline={"Wie heißen Sie?"} showDiverseGenderOption />
                {_includes(["enforceClaim", "defendClaim"], values.caseCategory) && (
                  <Address path="customer.person.residenceAddress" headline={"Wo wohnen Sie?"} />
                )}
                <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
                  <FormLegend>Wie können wir Sie erreichen?</FormLegend>
                </Grid>
                <Grid item xs={12}>
                  <ValidatorTextField
                    name="customer.telephone"
                    label="Ihre Telefonnummer"
                    autoComplete="tel"
                    validators={[
                      requiredValidator("Bitte geben Sie Ihre Telefonnummer an"),
                      {
                        validator: minStringLength,
                        params: [7],
                        message: "Ihre Telefonnummer ist zu kurz",
                      },
                      { validator: isPhoneNumber, message: "Ihre Telefonnummer enthält ungültige Zeichen" },
                    ]}
                  />
                </Grid>
                {_includes(["enforceClaim", "defendClaim"], values.caseCategory) && (
                  <>
                    <Grid item xs={12}>
                      <Group
                        type="radio"
                        name="affectedPartyType"
                        question={"Um wen geht es bei Ihrem Anliegen?"}
                        options={[
                          {
                            labelText: "Ich bin selbst betroffen und benötige rechtliche Unterstützung",
                            labelIcon: <PersonIcon />,
                            labelIconActive: <PersonIcon active />,
                            value: "customer",
                          },
                          {
                            labelText: "Eine mir bekannte Person ist betroffen und benötigt rechtliche Unterstützung - zum Beispiel ein Familienmitglied",
                            labelIcon: <FamilyIcon />,
                            labelIconActive: <FamilyIcon active />,
                            value: "coinsured",
                          },
                        ]}
                        validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                      />
                    </Grid>

                    {values.affectedPartyType === "coinsured" && (
                      <Grid item xs={12}>
                        <Group
                          type="radio"
                          name="clientWasMinorAtContractDate"
                          question={"Ist die Person zum gegenwärtigen Zeitpunkt minderjährig?"}
                          options={[
                            {
                              labelText: "Ja",
                              labelIcon: <CheckIcon />,
                              labelIconActive: <CheckIcon active />,
                              value: "yes",
                            },
                            {
                              labelText: "Nein",
                              labelIcon: <CrossIcon />,
                              labelIconActive: <CrossIcon active />,
                              value: "no",
                            },
                          ]}
                          validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                        />
                      </Grid>
                    )}
                    {values.affectedPartyType === "coinsured" && (
                      <>
                        <Person path="client" headline="Wie heißt diese Person?" showDiverseGenderOption />
                        {values.clientWasMinorAtContractDate === "yes" && (
                          <Grid item xs={12}>
                            <ValidatorDateField
                              name="client.birthDate"
                              label="Geburtstag"
                              validators={[
                                requiredFieldDefault,
                                dateFieldDefault,
                                pastOrSameDateDefault,
                                {
                                  validator: isLessThen18YearsAgo,
                                  message: "Die Person ist bereits volljährig. Bitte Datum anpassen oder vorherige Frage mit “Nein” beantworten",
                                },
                              ]}
                            />
                          </Grid>
                        )}
                        <Address path="client.residenceAddress" headline={"Wo wohnt diese Person?"} />
                        {values.clientWasMinorAtContractDate === "yes" && (
                          <>
                            <Grid item xs={12}>
                              <Group
                                type="radio"
                                name="clientLegalRepresentativeAmount"
                                modalContent={
                                  <>
                                    <Typography paragraph>
                                      Wenn die betroffene Person zu diesem Zeitpunkt minderjährig war, müssen die gesetzlichen Vertreter Legalbird eine
                                      Vollmacht zur rechtlichen Vertretung der minderjährigen Person erteilen.
                                    </Typography>
                                    <Typography paragraph>
                                      Als Eltern sind Sie Ihrem Kind gegenüber in der Regel gesamtvertretungsberechtigt. Das heißt, beide Elternteile müssen
                                      gemeinsam die Zustimmung erteilen. Sollte ein Elternteil verstorben sein oder ihm wurde das Sorgerecht entzogen, tragen
                                      Sie bitte nur das andere Elternteil als gesetzliche Vertretungsperson ein. Wenn Sie vom anderen Elternteil getrennt leben
                                      und beide einzelberechtigt sind, wird am Ende nur die Unterschrift der Person abgefragt, die als erste gesetzliche
                                      Vertretung eingetragen ist.
                                    </Typography>
                                  </>
                                }
                                question={translate(`${product.name}.clientLegalRepresentativeAmount.question`, values)}
                                options={[
                                  {
                                    labelText: translate(`${product.name}.clientLegalRepresentativeAmount.options.1.label`, values),
                                    labelIcon: <PersonIcon />,
                                    labelIconActive: <PersonIcon active />,
                                    value: "1",
                                  },
                                  {
                                    labelText: translate(`${product.name}.clientLegalRepresentativeAmount.options.2.label`, values),
                                    labelIcon: <OtherPersonIcon />,
                                    labelIconActive: <OtherPersonIcon active />,
                                    value: "2",
                                  },
                                ]}
                                validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <Group
                                type="radio"
                                name="legalRepresentativeType"
                                question={translate(`${product.name}.legalRepresentativeType.question`, values)}
                                options={[
                                  {
                                    labelText: translate(`${product.name}.legalRepresentativeType.options.me.label`, values),
                                    labelIcon: values.clientLegalRepresentativeAmount == "2" ? <CheckIcon /> : <PersonIcon />,
                                    labelIconActive: values.clientLegalRepresentativeAmount == "2" ? <CheckIcon active /> : <PersonIcon active />,
                                    value: "me",
                                  },
                                  {
                                    labelText: translate(`${product.name}.legalRepresentativeType.options.other.label`, values),
                                    labelIcon: values.clientLegalRepresentativeAmount == 2 ? <CrossIcon /> : <OtherPersonIcon />,
                                    labelIconActive: values.clientLegalRepresentativeAmount == 2 ? <CrossIcon active /> : <OtherPersonIcon active />,
                                    value: "other",
                                  },
                                ]}
                                validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                              />
                            </Grid>
                            <Person path="clientLegalRepresentative" headline={translate(`${product.name}.clientLegalRepresentative.question`, values)} />
                            <Address
                              path="clientLegalRepresentative.residenceAddress"
                              headline={translate(`${product.name}.clientLegalRepresentative.residenceAddress.question`, values)}
                            />
                            {values.clientLegalRepresentativeAmount === "2" && (
                              <>
                                <Person
                                  path="secondClientLegalRepresentative"
                                  headline={translate(`${product.name}.secondClientLegalRepresentative.question`, values)}
                                />
                                <Address
                                  path="secondClientLegalRepresentative.residenceAddress"
                                  headline={translate(`${product.name}.secondClientLegalRepresentative.residenceAddress.question`, values)}
                                />

                                <Grid item xs={12}>
                                  <Group
                                    type="radio"
                                    name="clientLegalRepresentativesAuthorizationType"
                                    question={translate(`${product.name}.clientLegalRepresentativesAuthorizationType.question`, values)}
                                    options={[
                                      {
                                        labelText: translate(`${product.name}.clientLegalRepresentativesAuthorizationType.options.both.label`, values),
                                        labelIcon: <OtherPersonIcon />,
                                        labelIconActive: <OtherPersonIcon active />,
                                        value: "both",
                                      },
                                      {
                                        labelText: translate(`${product.name}.clientLegalRepresentativesAuthorizationType.options.individually.label`, values),
                                        labelIcon: <PersonIcon />,
                                        labelIconActive: <PersonIcon active />,
                                        value: "individually",
                                      },
                                    ]}
                                    validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                                  />
                                </Grid>
                              </>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </>
                )}
                {values.caseCategory === "enforceClaim" && <BaseEnforceClaim />}
              </>
            )}
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
